import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Plus, Copy, Check, Trash2, KeyRound, Loader2, Mail } from "lucide-react";

const STATUS = {
  nuova: { label: "Nuova", cls: "bg-amber-100 text-amber-700" },
  in_lavorazione: { label: "In lavorazione", cls: "bg-blue-100 text-blue-700" },
  fatta: { label: "Fatta", cls: "bg-emerald-100 text-emerald-700" },
};
const PRIO = { alta: "text-red-600", media: "text-amber-600", bassa: "text-slate-500" };

export default function Clienti() {
  const [tab, setTab] = useState("codici");
  const [codes, setCodes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState("");

  const load = async () => {
    try {
      const [c, r] = await Promise.all([api.get("/client-codes"), api.get("/client-requests")]);
      setCodes(c.data);
      setRequests(r.data);
    } catch {
      toast.error("Caricamento non riuscito");
    }
  };
  useEffect(() => { load(); }, []);

  const createCode = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Inserisci il nome del cliente"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/client-codes", { name: name.trim(), email: email.trim(), origin: window.location.origin });
      setCodes((prev) => [data, ...prev]);
      setName(""); setEmail("");
      toast.success(data.email_sent ? `Codice ${data.code} inviato a ${data.email}` : `Codice creato: ${data.code}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Creazione non riuscita");
    } finally { setCreating(false); }
  };

  const copyCode = async (c) => {
    try { await navigator.clipboard.writeText(c); setCopied(c); toast.success("Codice copiato"); setTimeout(() => setCopied(""), 1800); }
    catch { toast.error("Copia non riuscita"); }
  };

  const removeCode = async (id) => {
    if (!window.confirm("Eliminare questo codice cliente?")) return;
    await api.delete(`/client-codes/${id}`);
    setCodes((prev) => prev.filter((x) => x.id !== id));
    toast.success("Codice eliminato");
  };

  const setStatus = async (id, status) => {
    await api.patch(`/client-requests/${id}/status`, { status });
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const newCount = requests.filter((r) => r.status === "nuova").length;

  return (
    <div className="space-y-8" data-testid="clienti-page">
      <div>
        <h1 className="font-head text-3xl font-bold text-navy">Clienti & Richieste</h1>
        <p className="mt-1 text-sm text-slate-500">Crea i codici cliente e gestisci le richieste su misura arrivate dalla pagina web.</p>
      </div>

      <div className="flex gap-2">
        <button data-testid="tab-codici" onClick={() => setTab("codici")} className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === "codici" ? "bg-navy text-white" : "bg-white text-navy hover:bg-slate-100"}`}>
          Codici cliente <span className="ml-1 opacity-60">{codes.length}</span>
        </button>
        <button data-testid="tab-richieste" onClick={() => setTab("richieste")} className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === "richieste" ? "bg-navy text-white" : "bg-white text-navy hover:bg-slate-100"}`}>
          Richieste <span className="opacity-60">{requests.length}</span>
          {newCount > 0 && <span className="rounded-full bg-gold px-2 py-0.5 text-xs font-bold text-black">{newCount} nuove</span>}
        </button>
      </div>

      {tab === "codici" && (
        <div className="space-y-6">
          <form onSubmit={createCode} className="flex flex-col gap-3 rounded-2xl border border-navy/10 bg-white p-6 shadow-sm sm:flex-row sm:items-end" data-testid="create-code-form">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Nome cliente</label>
              <input data-testid="code-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. M/Y Serenity" className="h-12 w-full rounded-lg border-2 border-slate-200 px-4 outline-none focus:border-gold" />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Email (opzionale)</label>
              <input data-testid="code-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@email.com" className="h-12 w-full rounded-lg border-2 border-slate-200 px-4 outline-none focus:border-gold" />
            </div>
            <button data-testid="create-code-btn" type="submit" disabled={creating} className="flex h-12 items-center justify-center gap-2 rounded-lg bg-navy px-6 font-bold text-white transition-colors hover:bg-navy-hover disabled:opacity-60">
              {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />} Genera codice
            </button>
          </form>

          {codes.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">Nessun codice cliente ancora.</div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {codes.map((c) => (
                <div key={c.id} data-testid={`code-card-${c.code}`} className="rounded-2xl border border-navy/10 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="truncate font-head text-lg font-semibold text-navy">{c.name}</div>
                      {c.email && <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-400"><Mail className="h-3 w-3" /> {c.email}</div>}
                    </div>
                    <button data-testid={`delete-code-${c.code}`} onClick={() => removeCode(c.id)} className="text-slate-300 transition-colors hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-4 flex items-center justify-between rounded-lg bg-navy px-4 py-3">
                    <span className="flex items-center gap-2 font-mono text-lg font-bold tracking-widest text-gold"><KeyRound className="h-4 w-4" /> {c.code}</span>
                    <button data-testid={`copy-code-${c.code}`} onClick={() => copyCode(c.code)} className="text-white/70 transition-colors hover:text-white">
                      {copied === c.code ? <Check className="h-5 w-5 text-gold" /> : <Copy className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "richieste" && (
        <div className="space-y-3">
          {requests.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">Nessuna richiesta ricevuta.</div>
          ) : (
            requests.map((r) => (
              <div key={r.id} data-testid={`request-${r.id}`} className="rounded-2xl border border-navy/10 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-gold" />
                    <span className="font-head font-semibold text-navy">{r.client_name}</span>
                    <span className="font-mono text-xs text-slate-400">{r.code}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{r.category}</span>
                    <span className={`text-xs font-semibold uppercase ${PRIO[r.priority] || "text-slate-500"}`}>Priorità {r.priority}</span>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS[r.status]?.cls || "bg-slate-100 text-slate-600"}`}>{STATUS[r.status]?.label || r.status}</span>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{r.description}</p>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
                  <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 text-sm text-navy hover:text-gold"><Mail className="h-4 w-4" /> {r.email}</a>
                  <div className="flex gap-1.5">
                    {Object.keys(STATUS).map((s) => (
                      <button key={s} data-testid={`status-${r.id}-${s}`} onClick={() => setStatus(r.id, s)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${r.status === s ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        {STATUS[s].label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
